
import { Vue, Options, prop } from "vue-class-component";
import { debounce } from "lodash";
import AdvancedFilter from "@/app/ui/components/advanced-filter/index.vue";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { CargoV2ListRequest } from "@/data/payload/api/CargoV2ApiRequest";

function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function defaultStartDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toLocalDateString(d);
}

function defaultEndDate(): string {
  return toLocalDateString(new Date());
}

interface AdvancedFilterData {
  cityDestinationCode?: string;
  airport?: string;
  hubIdSelected?: { label: string; value: number }[];
}

class Props {
  summary = prop<{
    plane?: number | string;
    truck?: number | string;
    ship?: number | string;
  }>({
    default: () => ({ plane: 0, truck: 0, ship: 0 })
  });
  initialFilter = prop<Partial<CargoV2ListRequest>>({ default: () => ({}) });
  isSuccessBookingTab = prop<boolean>({ default: true, type: Boolean });
}

@Options({
  components: { AdvancedFilter },
  emits: ["change"],
  watch: {
    initialFilter: {
      handler(f: Partial<CargoV2ListRequest>) {
        if (f) {
          this.localSearch = f.search ?? this.localSearch;
          if (f.cargoTypes) {
            this.localCargoTypes = f.cargoTypes
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
          } else if (f.cargoType !== undefined) {
            this.localCargoTypes = f.cargoType ? [f.cargoType] : [];
          }
          if (f.startDate) this.localStartDate = f.startDate;
          if (f.endDate) this.localEndDate = f.endDate;
          if (f.destinationCity !== undefined)
            this.advancedFilterData.cityDestinationCode = f.destinationCity;
          if (f.destinationAirport !== undefined)
            this.advancedFilterData.airport = f.destinationAirport;
          if (f.hubIds !== undefined) {
            this.advancedFilterData.hubIdSelected = f.hubIds
              ? f.hubIds
                  .split(",")
                  .map((id) => ({ label: id.trim(), value: Number(id.trim()) }))
              : [];
          }
        }
      },
      deep: true
    }
  }
})
export default class CargoV2FilterSection extends Vue.with(Props) {
  get modaOptions() {
    return [
      { name: this.$t("cargo.filters.cargoTypes.plane"), value: "plane" },
      { name: this.$t("cargo.filters.cargoTypes.truck"), value: "truck" },
      { name: this.$t("cargo.filters.cargoTypes.ship"), value: "ship" }
    ];
  }

  localSearch = "";
  localCargoTypes: string[] = [];
  localStartDate = "";
  localEndDate = "";

  advancedFilterData: AdvancedFilterData = {
    cityDestinationCode: "",
    airport: "",
    hubIdSelected: []
  };

  created(): void {
    const f = this.initialFilter;
    this.localSearch = f.search ?? "";
    if (f.cargoTypes) {
      this.localCargoTypes = f.cargoTypes
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (f.cargoType) {
      this.localCargoTypes = [f.cargoType];
    } else {
      this.localCargoTypes = [];
    }
    this.advancedFilterData.cityDestinationCode = f.destinationCity ?? "";
    this.advancedFilterData.airport = f.destinationAirport ?? "";
    const hubData = getHubData();
    this.advancedFilterData.hubIdSelected = f.hubIds
      ? f.hubIds
          .split(",")
          .map((id) => ({ label: id.trim(), value: Number(id.trim()) }))
      : hubData?.hubId
      ? [{ label: hubData.hubName, value: hubData.hubId }]
      : [];
    if (!this.localStartDate || !this.localEndDate) {
      this.localStartDate = defaultStartDate();
      this.localEndDate = defaultEndDate();
    }
  }

  mounted(): void {
    if (!this.localStartDate || !this.localEndDate) {
      this.localStartDate = defaultStartDate();
      this.localEndDate = defaultEndDate();
    }
  }

  isModaSelected(value: string): boolean {
    return this.localCargoTypes.includes(value);
  }

  modaCount(value: string): string {
    const s = this.summary;
    switch (value) {
      case "plane":
        return `(${Number(s?.plane) || 0})`;
      case "truck":
        return `(${Number(s?.truck) || 0})`;
      case "ship":
        return `(${Number(s?.ship) || 0})`;
      default:
        return "";
    }
  }

  emitChange = debounce(() => {
    const hubIds = this.advancedFilterData.hubIdSelected?.length
      ? this.advancedFilterData.hubIdSelected
          .map((x) => String(x.value))
          .join(",")
      : "";
    const cargoTypesStr = this.localCargoTypes.join(",");
    this.$emit("change", {
      search: this.localSearch,
      cargoType: this.localCargoTypes[0] ?? "",
      cargoTypes: cargoTypesStr,
      startDate: this.localStartDate,
      endDate: this.localEndDate,
      destinationCity: this.advancedFilterData.cityDestinationCode ?? "",
      destinationAirport: this.advancedFilterData.airport ?? "",
      hubIds
    } as CargoV2ListRequest);
  }, 250);

  onChangeAdvancedFilter(e: AdvancedFilterData) {
    this.advancedFilterData.cityDestinationCode = e.cityDestinationCode ?? "";
    this.advancedFilterData.airport = e.airport ?? "";
    this.advancedFilterData.hubIdSelected = e.hubIdSelected ?? [];
    this.emitChange();
  }

  onResetAdvancedFilter(isFetch: boolean): void {
    this.advancedFilterData.cityDestinationCode = "";
    this.advancedFilterData.airport = "";
    this.advancedFilterData.hubIdSelected = [];
    if (isFetch) this.emitChange();
  }

  onSearch(value: string): void {
    this.localSearch = value;
    this.$emit("search", this.localSearch);
  }

  onClearSearch(): void {
    this.localSearch = "";
    this.$emit("search", "");
  }

  onFilterModa(value: string): void {
    const idx = this.localCargoTypes.indexOf(value);
    if (idx === -1) {
      this.localCargoTypes = [...this.localCargoTypes, value];
    } else {
      this.localCargoTypes = this.localCargoTypes.filter((v) => v !== value);
    }
    this.emitChange();
  }

  setPeriode(dates: [Date | string | null, Date | string | null]): void {
    const [start, end] = dates;
    if (!start || !end) return;
    const startDate = start instanceof Date ? start : new Date(start);
    const endDate = end instanceof Date ? end : new Date(end);
    this.localStartDate = startDate.toISOString().slice(0, 10);
    this.localEndDate = endDate.toISOString().slice(0, 10);
    this.emitChange();
  }
}
